/**
 * API Configuration
 * 
 * This file contains important API URLs and endpoints that are used
 * throughout the application for development and maintenance.
 */

// API Environment Options
export const API_ENVIRONMENTS = {
  DEVELOPMENT: 'https://dev-api.nodus.foo/api',
  STAGING: 'https://nodus.foo/api', 
  PRODUCTION: 'https://api.nodus.se/api',
} as const;

// Get current API base URL (supports runtime switching in development)
const getCurrentBaseUrl = () => {
  // In development mode, always use development API
  if (import.meta.env.DEV) {
    // Check for runtime override from dev tools
    if (typeof window !== 'undefined') {
      const devApiOverride = localStorage.getItem('dev-api-override');
      if (devApiOverride && Object.values(API_ENVIRONMENTS).includes(devApiOverride as any)) {
        return devApiOverride;
      }
    }
    
    // Always use development API in dev mode
    return API_ENVIRONMENTS.DEVELOPMENT;
  }
  
  // Use environment variables if available
  const domain = import.meta.env.VITE_BASE_URL_DOMAIN || 'nodus.foo';
  const protocol = import.meta.env.VITE_BASE_URL_API_PROTOCOL || 'https';
  const path = import.meta.env.VITE_BASE_URL_API_PATH || '/api';
  
  return `${protocol}://${domain}${path}`;
};

export const API_CONFIG = {
  // Base API URL - dynamically determined
  get BASE_URL() {
    return getCurrentBaseUrl();
  },
  
  // OpenAPI specification URL - dynamically determined 
  get OPENAPI_SPEC_URL() {
    return `${this.BASE_URL}/openapi.json`;
  },
  
  // Important endpoints for reference
  ENDPOINTS: {
    EXTERNAL_IPS: '/crm/external-ips/',
    NETWORKS: '/crm/networks/',
    SPEED_LIMITS: '/crm/networks/speedlimits/',
    SITES: '/crm/sites/',
    ADDRESSES: '/crm/addresses/',
    FLOORS: '/crm/floors/',
    ROOMS: '/crm/rooms/',
    TENANTS: '/crm/tenants/',
    OUTLETS: '/crm/outlets/',
    WIRELESS: '/crm/wireless/',
    USERS: '/crm/users/',
    INTERFACES: '/crm/interfaces/',
    SEARCH: '/search/',
    SEARCH_TYPES: '/search/types/',
    TIMEZONES: '/core/timezones/',
    DEVICE_ROLES: '/crm/devices/roles/',
    MIKROTIK_DEVICES: '/mikrotik/devices/',
    MIKROTIK_DEVICE_SYNC: '/mikrotik/devices/{device_id}/sync/',
    MIKROTIK_INTERFACES: '/mikrotik/interfaces/',
    MIKROTIK_DHCP_POOLS: '/mikrotik/dhcp/pools/',
    MIKROTIK_DHCP_SERVERS: '/mikrotik/dhcp/servers/',
    MIKROTIK_DHCP_LEASES: '/mikrotik/dhcp/leases/',
    MIKROTIK_CREDENTIALS: '/mikrotik/credentials/',
    ARISTA_DEVICES: '/arista/devices/',
    ARISTA_DEVICE_SYNC: '/arista/devices/{device_id}/sync/',
    ARISTA_INTERFACES: '/arista/interfaces/',
    ARISTA_CREDENTIALS: '/arista/credentials/',
    /** @deprecated Use NETWORK_DEVICES for new code. */
    MAC_ADDRESSES: '/crm/networks/mac-addresses/',
    NETWORK_DEVICES: '/crm/networks/network-devices/',
    UNIFI_CLIENTS: '/unifi/clients/',
    UNIFI_RF: '/unifi/rf/',
    AXEMA_DEFAULT_TENANT_ACCESS: '/axema/default-tenant-access/',
    // Network diagnostics endpoints (renamed from speedtest/ to diagnostic/)
    NETWORK_DIAGNOSTICS_SESSIONS: '/network/diagnostic/sessions/',
    NETWORK_DIAGNOSTICS_CREATE_INTENT: '/network/diagnostic/sessions/create-intent/',
    NETWORK_DIAGNOSTICS_CREATE_SPEEDTEST: '/network/diagnostic/sessions/create-speedtest/',
    NETWORK_DIAGNOSTICS_TELEMETRY: '/network/diagnostic/telemetry/',
    NETWORK_DIAGNOSTICS_SERVERS: '/network/diagnostic/servers/',
  },
  
  // Schema references for type generation
  SCHEMAS: {
    EXTERNAL_IP_LIST: 'ExternalIPAddressListSchema',
    EXTERNAL_IP_DETAIL: 'ExternalIPAddressDetailSchema',
    EXTERNAL_IP_CREATE: 'ExternalIPAddressCreateSchema',
    NETWORK_LIST: 'NetworkListSchema',
    NETWORK_DETAIL: 'NetworkDetailSchema',
    NETWORK_CREATE: 'NetworkCreateSchema',
    NETWORK_UPDATE: 'NetworkUpdateSchema',
    MIKROTIK_DEVICE_LIST: 'DeviceListSchema',
    MIKROTIK_DHCP_POOL_LIST: 'DHCPPoolListSchema',
    MIKROTIK_DHCP_POOL_DETAIL: 'DHCPPoolDetailSchema',
    MIKROTIK_DHCP_SERVER_LIST: 'DHCPServerListSchema',
    MIKROTIK_DHCP_LEASE_LIST: 'DhcpLeaseSchema',
  }
} as const;

/**
 * Helper function to get the full OpenAPI spec
 * Usage: await fetchOpenAPISpec() to get the latest API specification
 */
export const fetchOpenAPISpec = async () => {
  try {
    const response = await fetch(API_CONFIG.OPENAPI_SPEC_URL);
    if (!response.ok) {
      throw new Error(`Failed to fetch OpenAPI spec: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching OpenAPI specification:', error);
    throw error;
  }
};

/**
 * Helper function to check if specific endpoints exist in the API
 * Usage: await validateEndpoint('/crm/external-ips/')
 */
export const validateEndpoint = async (endpoint: string): Promise<boolean> => {
  try {
    const spec = await fetchOpenAPISpec();
    return spec.paths && spec.paths[endpoint] !== undefined;
  } catch (error) {
    console.error(`Error validating endpoint ${endpoint}:`, error);
    return false;
  }
};

export default API_CONFIG;
